import { Delete } from '@mapped/rivet/dist/mui/icons'
import {
  Button,
  LinearProgress,
  Tooltip,
} from '@mapped/rivet/dist/mui/material'
import { styled } from '@mapped/rivet/dist/mui/styles'
import {
  PersonalAccessToken,
  PersonalAccessTokenUpdateResponse,
} from '@mapped/schema-graph-react-apollo'
import pluralize from 'pluralize'
import { FunctionComponent, useEffect, useState } from 'react'
import { useModals } from '../../../../hooks/useModals'
import { useNotifications } from '../../../../hooks/useNotifications'
import { EStepToken } from '../../../../types/general'
import GeneratedToken from './generatedToken'
import TokenDeleteConfirmationModal from './tokenDeleteConfirmationModal'
import TokenRegenerateConfirmationModal from './tokenRegenerateConfirmationModal'

const Step1: FunctionComponent<IStep1Props> = ({
  setCurrentStep,
  tokensList,
  tokenLoading,
}) => {
  const notifications = useNotifications()
  const modals = useModals()

  const [displayError, setDisplayError] = useState(false)
  const [regenerationResponse, setRegenerationResponse] =
    useState<PersonalAccessTokenUpdateResponse>()

  // Sort tokens alphabetically
  const sortedTokens = [...(tokensList || [])]?.sort((a: any, b: any) =>
    a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
  )

  useEffect(() => {
    if (displayError) {
      notifications.push({
        type: 'error',
        message: 'Whoops! Something went wrong..',
      })
    }
  }, [displayError])

  return (
    <div>
      <Button onClick={() => setCurrentStep(EStepToken.NEW)}>
        Generate Access Token
      </Button>

      {!!tokenLoading && <LinearProgress />}

      {!!sortedTokens?.length && (
        <TokenList>
          {sortedTokens.map((token: PersonalAccessToken, index: number) => (
            <li key={`row-token-${index}`}>
              <div>
                <TokenRow className="token-row">
                  <TokenItem data-testname={token.name}>
                    <span>{token.name}</span>

                    {token.permissions?.length && (
                      <Tooltip
                        key={index}
                        title={token.permissions?.join('\n')}
                        placement="right"
                      >
                        <Scopes>
                          {pluralize('scope', token.permissions?.length, true)}
                        </Scopes>
                      </Tooltip>
                    )}
                  </TokenItem>

                  <div style={{ marginLeft: 'auto', display: 'flex' }}>
                    <Regenerate
                      style={{ cursor: 'pointer' }}
                      className="btn-regenerate"
                      onClick={() => {
                        modals.open(TokenRegenerateConfirmationModal, {
                          tokenId: token.id!,
                          onError: () => setDisplayError(true),
                          onSuccess: (res: PersonalAccessTokenUpdateResponse) =>
                            setRegenerationResponse(res),
                        })
                      }}
                    >
                      Regenerate
                    </Regenerate>

                    <IconDelete>
                      <Delete
                        onClick={() => {
                          modals.open(TokenDeleteConfirmationModal, {
                            tokenId: token.id!,
                            onError: () => setDisplayError(true),
                          })
                        }}
                      />
                    </IconDelete>
                  </div>
                </TokenRow>

                {regenerationResponse?.pat?.id === token.id && (
                  <GeneratedToken newToken={regenerationResponse?.token!} />
                )}
              </div>
            </li>
          ))}
        </TokenList>
      )}
    </div>
  )
}

const TokenList = styled.ul`
  margin-left: 0;
  width: 100%;
  border: 1px solid #b8bfcc;
  margin-top: 60px;

  & > li {
    border-bottom: 1px solid #b8bfcc;
  }

  & > li:last-child {
    border-bottom: 0;
  }
`

const TokenRow = styled.div`
  display: flex;
  padding: 16px;
`

const TokenItem = styled.div`
  width: 70%;
  color: ${(props) => props.theme.palette.primary.main};
`

const Scopes = styled.span`
  color: ${(props) => props.theme.palette.common.white};
  background-color: ${(props) => props.theme.palette.text.a50};
  border-radius: 100px;
  margin-left: 20px;
  padding: 4px 16px;
  font-size: 12px;
  cursor: pointer;
`

const Regenerate = styled.div`
  color: ${(props) => props.theme.palette.error.light};
`

const IconDelete = styled.div`
  margin-left: 24px;
  cursor: pointer;

  & svg {
    color: ${(props) => props.theme.palette.text.a50};
  }

  &:hover {
    svg {
      color: ${(props) => props.theme.palette.error.light};
    }
  }
`

interface IStep1Props {
  setCurrentStep: (step: EStepToken) => void
  tokensList?: PersonalAccessToken[]
  tokenLoading?: boolean
}

export default Step1
